import React, { useEffect, useState } from 'react';
import { apiGet } from '../../lib/api';
import { inrCompact, formatDate } from '../../lib/format';
import { FileText, TrendingUp, Trophy, Target, IndianRupee, AlertTriangle, Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';

// Tendering_30Sep2026 §1: this dashboard now reads db.enquiries — the
// retired Tender Register (db.bid_tenders) is no longer a data source.
const STAGE_COLORS = {
  'Lead Identified': 'bg-slate-100 text-slate-700',
  'New Enquiry': 'bg-slate-100 text-slate-700',
  'Provisional Qualification': 'bg-blue-100 text-blue-700',
  'Site Visit': 'bg-cyan-100 text-cyan-700',
  'Costing and Documentation': 'bg-amber-100 text-amber-700',
  'Proposal Submitted': 'bg-orange-100 text-orange-700',
  Negotiation: 'bg-purple-100 text-purple-700',
  Won: 'bg-emerald-100 text-emerald-700',
  Awarded: 'bg-emerald-100 text-emerald-700',
  Lost: 'bg-red-100 text-red-700',
  Parked: 'bg-slate-100 text-slate-500',
  'On Hold': 'bg-slate-100 text-slate-500',
};

// Bar / dot colour per urgency bucket (computed server-side)
const URGENCY_COLORS = {
  overdue:     { bar: 'bg-red-500',    text: 'text-red-700',    bg: 'bg-red-50',    border: 'border-red-200',    label: 'Overdue' },
  today:       { bar: 'bg-amber-500',  text: 'text-amber-700',  bg: 'bg-amber-50',  border: 'border-amber-200',  label: 'Due Today' },
  tomorrow:    { bar: 'bg-orange-500', text: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200', label: 'Due Tomorrow' },
  future:      { bar: 'bg-emerald-500',text: 'text-emerald-700',bg: 'bg-emerald-50',border: 'border-emerald-200',label: 'On Track' },
  unscheduled: { bar: 'bg-slate-300',  text: 'text-slate-500',  bg: 'bg-slate-50',  border: 'border-slate-200',  label: 'No Deadline' },
};

export default function EstimationDashboard() {
  const [kpis, setKpis] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [alertsOpen, setAlertsOpen] = useState(true);

  useEffect(() => {
    apiGet('/enquiries/dashboard').then(setKpis).catch(console.error);
    apiGet('/enquiries/submission-alerts').then(setAlerts).catch(console.error);
  }, []);

  if (!kpis) return <div className="p-8 text-slate-500">Loading dashboard…</div>;

  const cards = [
    { label: 'Total Enquiries', value: kpis.total_enquiries, icon: FileText, color: 'text-slate-700' },
    { label: 'Pipeline Value', value: inrCompact(kpis.pipeline_value_cr * 1e7), icon: TrendingUp, color: 'text-blue-600' },
    { label: 'Won Value', value: inrCompact(kpis.won_value_cr * 1e7), icon: Trophy, color: 'text-emerald-600' },
    { label: 'Win Rate', value: `${kpis.win_rate_pct}%`, icon: Target, color: 'text-[#3b4872]' },
  ];

  const hasUrgent = alerts && (alerts.today_count > 0 || alerts.tomorrow_count > 0 || alerts.overdue_count > 0);
  const recent = kpis.recent_enquiries || [];

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="estimation-dashboard">
      <div>
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">Bid & Estimation</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Enquiry Pipeline</h1>
        <p className="text-sm text-slate-600 mt-1">Sourced from the Enquiry Tracker — the single record for every tender from lead to award.</p>
      </div>

      {/* Submission Alerts Banner */}
      {hasUrgent && (
        <div className="bg-white border-l-4 border-amber-500 border-y border-r border-slate-200 rounded-lg shadow-sm" data-testid="submission-alerts-banner">
          <button
            onClick={() => setAlertsOpen(o => !o)}
            className="w-full px-4 py-3 flex items-center gap-3 hover:bg-amber-50/40 transition-colors"
            data-testid="alerts-banner-toggle"
          >
            <AlertTriangle className="text-amber-600 shrink-0" size={18} />
            <div className="flex-1 text-left text-sm">
              <span className="font-bold text-slate-800">Submission deadlines </span>
              {alerts.overdue_count > 0 && (
                <span className="text-red-700"><b>{alerts.overdue_count}</b> overdue · </span>
              )}
              {alerts.today_count > 0 && (
                <span className="text-amber-700"><b>{alerts.today_count}</b> due TODAY · </span>
              )}
              {alerts.tomorrow_count > 0 && (
                <span className="text-orange-700"><b>{alerts.tomorrow_count}</b> due TOMORROW</span>
              )}
            </div>
            {alertsOpen ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
          </button>
          {alertsOpen && (
            <div className="border-t border-slate-200 px-4 py-3 space-y-2" data-testid="alerts-banner-body">
              {[
                { bucket: 'overdue', rows: alerts.overdue },
                { bucket: 'today', rows: alerts.today },
                { bucket: 'tomorrow', rows: alerts.tomorrow },
              ].filter(g => g.rows.length > 0).map(g => (
                <div key={g.bucket}>
                  <div className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${URGENCY_COLORS[g.bucket].text}`}>
                    {URGENCY_COLORS[g.bucket].label} ({g.rows.length})
                  </div>
                  <div className="space-y-1">
                    {g.rows.map(r => (
                      <Link
                        key={r.id}
                        to={`/app/estimation/enquiries/${r.id}`}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded text-xs border ${URGENCY_COLORS[g.bucket].border} ${URGENCY_COLORS[g.bucket].bg} hover:opacity-80`}
                        data-testid={`alert-row-${r.id}`}
                      >
                        <Calendar size={11} className={URGENCY_COLORS[g.bucket].text} />
                        <span className="font-mono font-bold">{r.enquiry_no}</span>
                        <span className="text-slate-700">{r.client}</span>
                        <span className="text-slate-500 truncate">{r.project_name}</span>
                        <span className="ml-auto px-1.5 py-0.5 rounded bg-white border border-slate-200 text-[9px] uppercase tracking-wider text-slate-600">
                          {r.stage}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map(c => (
          <div key={c.label} className="bg-white border border-slate-200 p-5 rounded-lg" data-testid={`est-kpi-${c.label.replace(/\s+/g,'-').toLowerCase()}`}>
            <div className="flex items-center justify-between mb-3">
              <c.icon className={`${c.color}`} size={20} />
            </div>
            <div className="text-2xl font-display font-extrabold tracking-tight">{c.value}</div>
            <div className="text-xs text-slate-500 mt-1 uppercase tracking-wide">{c.label}</div>
          </div>
        ))}
      </div>

      {/* Submission Timeline */}
      {kpis.timeline && kpis.timeline.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="submission-timeline">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-sm font-semibold uppercase tracking-wide text-slate-600">Submission Timeline</div>
              <div className="text-[11px] text-slate-500 mt-0.5">Enquiry received → submission deadline · today marker shown · colour = urgency</div>
            </div>
            <div className="flex items-center gap-3 text-[10px]">
              {['overdue','today','tomorrow','future'].map(k => (
                <div key={k} className="flex items-center gap-1">
                  <span className={`w-2.5 h-2.5 rounded-full ${URGENCY_COLORS[k].bar}`} />
                  <span className="text-slate-600">{URGENCY_COLORS[k].label}</span>
                </div>
              ))}
            </div>
          </div>
          <TimelineBars rows={kpis.timeline} todayIso={kpis.as_of} />
        </div>
      )}

      {/* Stage Breakdown */}
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <div className="text-sm font-semibold mb-3 uppercase tracking-wide text-slate-600">By Stage</div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {Object.entries(kpis.by_stage).map(([k, v]) => (
            <div key={k} className={`px-3 py-3 rounded-lg ${STAGE_COLORS[k] || 'bg-slate-100 text-slate-600'}`}>
              <div className="text-xl font-display font-bold">{v}</div>
              <div className="text-[10px] uppercase tracking-wide mt-1">{k}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Enquiries */}
      <div className="bg-white border border-slate-200 rounded-lg">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="text-sm font-semibold uppercase tracking-wide text-slate-600">Recent Enquiries</div>
          <Link to="/app/estimation/enquiries" className="text-xs text-[#3b4872] font-semibold hover:underline" data-testid="view-all-enquiries-link">View All →</Link>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">Enquiry No</th>
              <th className="text-left px-4 py-2 font-semibold">Client</th>
              <th className="text-left px-4 py-2 font-semibold">Project</th>
              <th className="text-right px-4 py-2 font-semibold">Est. Value</th>
              <th className="text-left px-4 py-2 font-semibold">Stage</th>
            </tr>
          </thead>
          <tbody>
            {recent.length === 0 ? (
              <tr><td colSpan={5} className="text-center text-slate-400 py-8">No enquiries yet — go to the Enquiry Tracker to add one.</td></tr>
            ) : recent.map(e => (
              <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3"><Link to={`/app/estimation/enquiries/${e.id}`} className="text-[#3b4872] font-medium hover:underline">{e.enquiry_no}</Link></td>
                <td className="px-4 py-3 text-slate-700">{e.client}</td>
                <td className="px-4 py-3 text-slate-600 max-w-xs truncate">{e.project_name}</td>
                <td className="px-4 py-3 text-right font-medium">{e.estimated_value_cr ? inrCompact(e.estimated_value_cr * 1e7) : '—'}</td>
                <td className="px-4 py-3"><span className={`px-2 py-1 rounded text-xs font-medium ${STAGE_COLORS[e.stage] || ''}`}>{e.stage}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* AI badge */}
      <div className="bg-gradient-to-r from-[#3b4872] to-[#5d6b9b] text-white rounded-lg p-5 flex items-center gap-4" data-testid="ai-badge">
        <div className="flex-shrink-0 w-12 h-12 bg-white/15 rounded-full flex items-center justify-center">
          <IndianRupee size={22} />
        </div>
        <div>
          <div className="font-display font-bold text-lg">AI-Powered Rate Analysis</div>
          <div className="text-sm text-white/80 mt-1">Library-first matching • Ellora AI fallback • Library auto-learns from every analysis</div>
        </div>
      </div>
    </div>
  );
}

function TimelineBars({ rows, todayIso }) {
  // Compute a shared date axis spanning the earliest received date to the
  // latest submission deadline so all bars share scale.
  const today = todayIso ? new Date(todayIso) : new Date();
  const allDates = [];
  rows.forEach(r => {
    if (r.received_date) allDates.push(new Date(r.received_date));
    if (r.submission_date) allDates.push(new Date(r.submission_date));
  });
  allDates.push(today);
  if (!allDates.length) return null;
  const minDate = new Date(Math.min(...allDates.map(d => d.getTime())));
  const maxDate = new Date(Math.max(...allDates.map(d => d.getTime())));
  // Pad ±2 days for visual breathing room.
  minDate.setDate(minDate.getDate() - 2);
  maxDate.setDate(maxDate.getDate() + 2);
  const totalMs = maxDate.getTime() - minDate.getTime();
  const pct = (d) => totalMs > 0 ? ((new Date(d).getTime() - minDate.getTime()) / totalMs) * 100 : 0;
  const fmt = (d) => formatDate(d);

  return (
    <div className="space-y-2" data-testid="timeline-bars">
      {/* Axis ruler */}
      <div className="flex items-center gap-3 text-[10px] text-slate-500 ml-[34%] pl-2">
        <span>{fmt(minDate)}</span>
        <div className="flex-1 border-t border-dashed border-slate-200 relative">
          <div className="absolute -top-1 w-px h-3 bg-rose-500" style={{ left: `${pct(today)}%` }} title="Today" />
          <div className="absolute -top-3.5 text-[9px] font-bold text-rose-600" style={{ left: `calc(${pct(today)}% - 8px)` }}>TODAY</div>
        </div>
        <span>{fmt(maxDate)}</span>
      </div>
      {rows.map(r => {
        const col = URGENCY_COLORS[r.urgency] || URGENCY_COLORS.unscheduled;
        const leftPct = r.received_date ? pct(r.received_date) : 0;
        const rightPct = r.submission_date ? pct(r.submission_date) : leftPct + 1;
        const widthPct = Math.max(rightPct - leftPct, 1);
        const daysLabel =
          r.days_remaining == null ? '—' :
          r.days_remaining < 0 ? `${Math.abs(r.days_remaining)}d overdue` :
          r.days_remaining === 0 ? 'today' :
          r.days_remaining === 1 ? 'tomorrow' :
          `${r.days_remaining}d left`;
        return (
          <Link
            key={r.enquiry_id}
            to={`/app/estimation/enquiries/${r.enquiry_id}`}
            className="flex items-center gap-2 group"
            data-testid={`timeline-row-${r.enquiry_id}`}
          >
            <div className="w-[34%] min-w-0 pr-2 text-xs flex items-center gap-2">
              <span className="font-mono font-bold text-[#3b4872] group-hover:underline truncate">{r.enquiry_no}</span>
              <span className="text-slate-500 truncate hidden md:inline">{r.client}</span>
            </div>
            <div className="flex-1 relative h-5 bg-slate-50 rounded">
              {/* Today line */}
              <div className="absolute top-0 bottom-0 w-px bg-rose-400/70 z-10" style={{ left: `${pct(today)}%` }} />
              {/* Bar */}
              <div
                className={`absolute top-0.5 bottom-0.5 rounded ${col.bar} opacity-80 group-hover:opacity-100`}
                style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                title={`${r.enquiry_no}: ${fmt(r.received_date)} → ${fmt(r.submission_date)}`}
              />
              {/* Submission marker dot */}
              {r.submission_date && (
                <div
                  className={`absolute top-1 w-3 h-3 rounded-full ${col.bar} ring-2 ring-white`}
                  style={{ left: `calc(${rightPct}% - 6px)` }}
                />
              )}
            </div>
            <div className={`w-[80px] text-right text-[10px] font-semibold ${col.text}`}>
              {daysLabel}
            </div>
          </Link>
        );
      })}
    </div>
  );
}
